/**
 * Graduation Progress Utilities
 *
 * Helper functions for calculating and displaying graduation progress
 * for Meteora Dynamic Bonding Curve tokens.
 */

/**
 * On-chain migrationQuoteThreshold per DBC config, in lamports.
 *
 * Verified via dbcClient.state.getPoolConfig() readback on 2026-05-06.
 * Source files:
 *   - dextoro-dbc-deploy/deployment-result-v5.json (V5 active)
 *   - dextoro-dbc-deploy/deployment-result-v4-redeploy.json (V4 active)
 *   - live RPC readback for V2/V3/V4-misdeploy
 *
 * Use getGraduationThresholdSOL(config) to look up by pool config address;
 * any caller that has pool.config in scope should pass it for per-config
 * accuracy. Callers without config in scope fall back to V5_DEFAULT_THRESHOLD_SOL.
 */
export const THRESHOLD_BY_CONFIG_LAMPORTS: Record<string, number> = {
  // V5 active 2026-05-06 (8→40 SOL FDV, 50/50 splits, 50% migration fee)
  'CdvWviUTvkEvKrF4WrJ89HDLzWMCaPAP9dLyzTqL1aao': 14_619_519_795,
  // V7-stealth one-off 2026-05-10 (Dextoro coin only, $300→$100K MC, 30% supply on migration, 5% post-grad)
  '29oQKJkrj8dFqiaqf1ekWytYTcLUWqLAx4oFYRnkhXss': 377_451_666_666,
  // V4 previous default 2026-05-02 to 2026-05-06 (200bps, 30→400 SOL FDV)
  '5YAPBAiKiDnkpLpnjLK6XCpRKRoQqZxNex5mczcqCE1E': 87_883_429_777,
  // V4 misdeploy 2026-04-27 to 2026-05-02 (1% on-chain, pools stranded)
  'DiCQ5vCxghiqyTNNLZrGGpBD52taNi5FxW9uSKErkW4P': 87_883_429_777,
  // V3 retired 2026-04-27 (100bps)
  '2rQyPnj2VjWLZTEXve8tazSaQTsCNEfyzQHEjXXk3aZp': 87_883_429_777,
  // V2 retired (MECHA + legacy, lower threshold)
  '2AxDw9a1PbWJQHeAANkw1WmtWL88GMfJCtrdvQhcajXz': 56_363_000_498,
};

/**
 * Per-config FDV target at migration, denominated in SOL.
 *
 * Used by getGraduationMarketCapUSD's range-check assertion: computed MC ≈
 * MIGRATION_FDV_SOL_BY_CONFIG[config] × solPriceUSD. The check fires a
 * console.warn + Sentry event when the computed value falls outside 50%–200%
 * of expected, which loudly catches the next decimal-mismatch-style bug
 * (the 2026-05-10 e1bf81f bug returned 1/1000 of expected because v7-stealth's
 * 9-decimal tokenDecimal was being treated as 6).
 *
 * All values verified via on-chain getPoolConfig readback on 2026-05-10
 * (migrationFDV_SOL = getPriceFromSqrtPrice(migrationSqrt, tokenDecimal, 9) × 1e9).
 * v2 came in at 250 SOL (not the 400 originally guessed). Any new config MUST
 * be added here and to tokenDecimalByConfig in dbcClient.ts — see CLAUDE.md
 * "DBC config rotation" section.
 */
export const MIGRATION_FDV_SOL_BY_CONFIG: Record<string, number> = {
  'CdvWviUTvkEvKrF4WrJ89HDLzWMCaPAP9dLyzTqL1aao': 40,        // V5: 8→40 SOL FDV (tokenDecimal=6)
  '29oQKJkrj8dFqiaqf1ekWytYTcLUWqLAx4oFYRnkhXss': 1132.355,  // V7-stealth: $100K at SOL=$88.32 (tokenDecimal=9)
  '5YAPBAiKiDnkpLpnjLK6XCpRKRoQqZxNex5mczcqCE1E': 400,       // V4 redeploy: 30→400 SOL FDV (tokenDecimal=6)
  'DiCQ5vCxghiqyTNNLZrGGpBD52taNi5FxW9uSKErkW4P': 400,       // V4 misdeploy (tokenDecimal=6)
  '2rQyPnj2VjWLZTEXve8tazSaQTsCNEfyzQHEjXXk3aZp': 400,       // V3 (tokenDecimal=6)
  '2AxDw9a1PbWJQHeAANkw1WmtWL88GMfJCtrdvQhcajXz': 250,       // V2: MECHA + legacy (tokenDecimal=6)
};

/**
 * V5 active default. New launches go to V5. Used by callers that don't have
 * a pool.config in scope (static page copy, iterating hooks where the token
 * data shape lacks a dbc_config column).
 *
 * Trade-off: iterating token-list progress bars iterate ALL pools and have
 * no per-pool config knowledge. Until launched_tokens gains a dbc_config
 * column + backfill (Phase 6 follow-up), V4 legacy pools render with this
 * V5-rate threshold and visually cap at 100%. The affected pool universe is
 * small (V4 was active 2026-05-02 to 2026-05-06) and the alternative —
 * keeping the legacy 87.88 default — would break V5 progress display,
 * which is the active marketing surface.
 */
export const V5_DEFAULT_THRESHOLD_LAMPORTS = 14_619_519_795;
export const V5_DEFAULT_THRESHOLD_SOL = V5_DEFAULT_THRESHOLD_LAMPORTS / 1_000_000_000;

/**
 * @deprecated Prefer getGraduationThresholdSOL(pool.config) when pool config
 * is available. Resolves to V5_DEFAULT_THRESHOLD_SOL.
 */
export const DEFAULT_GRADUATION_THRESHOLD_SOL = V5_DEFAULT_THRESHOLD_SOL;

/**
 * @deprecated Use DEFAULT_GRADUATION_THRESHOLD_SOL or
 * getGraduationThresholdSOL(config) instead. Kept for backward compatibility
 * with existing imports.
 */
export const GRADUATION_THRESHOLD_SOL = DEFAULT_GRADUATION_THRESHOLD_SOL;

/**
 * Look up the graduation threshold (in SOL) for a specific DBC config.
 * Falls back to V5_DEFAULT_THRESHOLD_SOL if config is unknown or not provided.
 *
 * Accepts either a base58 string or a Solana web3 PublicKey (or anything with
 * a `.toBase58()` method) so callers don't need to import PublicKey just to
 * normalise the input.
 */
export function getGraduationThresholdSOL(
  config: string | { toBase58: () => string } | null | undefined
): number {
  if (!config) return V5_DEFAULT_THRESHOLD_SOL;
  const key = typeof config === 'string' ? config : config.toBase58();
  const lamports = THRESHOLD_BY_CONFIG_LAMPORTS[key];
  return lamports !== undefined ? lamports / 1_000_000_000 : V5_DEFAULT_THRESHOLD_SOL;
}

/**
 * Progress tier definitions with colors and labels
 */
export interface ProgressTier {
  min: number;
  max: number;
  color: string;
  label: string;
  bgColor: string;
}

export const PROGRESS_TIERS: ProgressTier[] = [
  { min: 0, max: 25, color: '#EF4444', label: 'Early Stage', bgColor: 'rgba(239, 68, 68, 0.15)' },
  { min: 25, max: 50, color: '#F97316', label: 'Building', bgColor: 'rgba(249, 115, 22, 0.15)' },
  { min: 50, max: 75, color: '#EAB308', label: 'Momentum', bgColor: 'rgba(234, 179, 8, 0.15)' },
  { min: 75, max: 100, color: '#22C55E', label: 'Almost There', bgColor: 'rgba(34, 197, 94, 0.15)' },
  { min: 100, max: Infinity, color: '#CCFF00', label: 'Graduated', bgColor: 'rgba(204, 255, 0, 0.15)' },
];

/**
 * Get the progress tier for a given percentage
 */
export function getProgressTier(progress: number): ProgressTier {
  for (const tier of PROGRESS_TIERS) {
    if (progress >= tier.min && progress < tier.max) {
      return tier;
    }
  }
  return PROGRESS_TIERS[PROGRESS_TIERS.length - 1];
}

/**
 * Get the color for a given progress percentage
 */
export function getProgressColor(progress: number): string {
  return getProgressTier(progress).color;
}

/**
 * Get the label for a given progress percentage
 */
export function getProgressLabel(progress: number): string {
  return getProgressTier(progress).label;
}

/**
 * Calculate graduation progress from SOL reserves.
 * Pass `config` for per-pool accuracy when available; else uses V5 default.
 */
export function calculateProgress(
  quoteReserveSol: number,
  config?: string | { toBase58: () => string } | null
): number {
  const threshold = getGraduationThresholdSOL(config);
  return Math.min(100, (quoteReserveSol / threshold) * 100);
}

/**
 * Calculate remaining SOL needed for graduation.
 * Pass `config` for per-pool accuracy when available; else uses V5 default.
 */
export function getRemainingForGraduation(
  quoteReserveSol: number,
  config?: string | { toBase58: () => string } | null
): number {
  const threshold = getGraduationThresholdSOL(config);
  return Math.max(0, threshold - quoteReserveSol);
}

/**
 * Format progress for display (e.g., "67.5%")
 */
export function formatProgress(progress: number): string {
  if (progress >= 100) return '100%';
  return `${progress.toFixed(2)}%`;
}
